import React from 'react'
import ContentLayout from '../../components/ContentLayout/ContentLayout'

const PrivacyPolicyPage = () => {
    return (
        <ContentLayout>
            <div className="mx-40 my-8">
                <h1 className="center font-bold text-2xl my-4">
                    LIFTONE PRIVACY POLICY
                </h1>
                <p className="my-4">
                    <i>Effective as of January 1, 2023</i>
                </p>

                <h1 className="font-bold text-lg my-4">INTRODUCTION</h1>
                <p className="mb-2">
                    LiftOne LLC and its parent, subsidiaries, and affiliates
                    (“LiftOne,” “we,” “our,” or “us”) respects the privacy of
                    your information. This Privacy Policy is designed to assist
                    you in understanding how we collect, use, share, and
                    safeguard your information.
                </p>
                <p className="mb-2">
                    This Privacy Policy applies to individuals who access our
                    websites and our mobile applications, including, but not
                    limited to VisionLink and ProductLink (collectively,
                    “Sites”) and the services provided through our Sites
                    (collectively, “Services”). This Privacy Policy also applies
                    to individuals who provide us with information on purchase
                    agreements, rental agreements, service agreements, credit
                    applications and other business documents, including but not
                    limited to information regarding machines, equipment, or
                    products (“Assets”), and who visit one or our locations
                    (“Contact Centers”).
                </p>
                <p className="mb-2">
                    From time to time, we may change this Privacy Policy. If we
                    do, we will post an amended version on this webpage. Please
                    review this Privacy Policy periodically.
                </p>
                <p className="mb-2">
                    This Privacy Policy covers the following topics:
                </p>
                <ol className="list-decimal px-20 font-bold mb-4">
                    <li className="pl-4">INFORMATION WE COLLECT</li>
                    <li className="pl-4">HOW WE COLLECT INFORMATION </li>
                    <li className="pl-4">HOW WE USE YOUR INFORMATION</li>
                    <li className="pl-4">HOW WE SHARE YOUR INFORMATION</li>
                    <li className="pl-4">
                        COOKIES AND OTHER TRACKING TECHNOLOGIES
                    </li>
                    <li className="pl-4">“DO NOT TRACK” SIGNALS</li>
                    <li className="pl-4">CHOICES ABOUT YOUR INFORMATION</li>
                    <li className="pl-4">SECURITY</li>
                    <li className="pl-4">THIRD PARTY LINKS</li>
                    <li className="pl-4">CHILDREN’S PRIVACY</li>
                    <li className="pl-4">NOTICE TO CALIFORNIA RESIDENTS</li>
                    <li className="pl-4">ACCESSIBILITY</li>
                    <li className="pl-4">HOW TO CONTACT US</li>
                    <li className="pl-4">HYSTER-YALE DATA GOVERNANCE</li>
                </ol>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    1. INFORMATION WE COLLECT
                </h1>
                <h2 className="font-bold text-base my-2">
                    Information You Provide When Using Our Sites and Services
                </h2>
                <p className="mb-2">
                    “Personal Information” is information that relates to an
                    identified or identifiable individual. We may collect the
                    following Personal Information that you voluntarily provide
                    to us in using our Sites and Services:
                </p>
                <h3 className="font-bold text-base ml-8 my-2">
                    LiftOne Site and Services
                </h3>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Become a Vendor.</i> To become a vendor for either
                        goods, services, transportation, or consultation, you
                        will apply through our DocuSign based application whose
                        privacy policy can be found{' '}
                        <a
                            href="https://origin.docusign.com/privacy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-links hover:underline"
                        >
                            here.
                        </a>{' '}
                        You will provide your name, email address, tax
                        classification, federal ID number, state location,
                        physical address, telephone number, tax information, and
                        any additional information you choose to provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Join Our Team.</i> To apply for a position with
                        LiftOne, you will provide your name, physical address,
                        email address, telephone number, and any additional
                        information you choose to provide in your resume or
                        cover letter.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Apply for Credit.</i> To apply for credit, you will
                        provide your name, telephone number, email address,
                        physical address, business federal tax ID and the name,
                        telephone number, email address, city and state of any
                        references you provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Entering a Contest or Promotion.</i> To enter a
                        contest or promotion we may collect your name, company,
                        phone number, email, address, city, and state.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Surveys.</i> When answering a survey, we may collect
                        your name, company, phone number, email, address, city,
                        and state.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Account.</i> To create a LiftOne account you
                        will provide your name, email address, telephone number,
                        and physical address. To sign into your account you will
                        provide your email address and password.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Sign Up for our Newsletter.</i> To sign up to our
                        newsletter you will provide your email address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Get a Quote.</i> To get a quote you will provide your
                        name, telephone number, email address, and physical
                        address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Rental Request.</i> To make a rental request, you
                        will provide your name, telephone number, email address,
                        and physical address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Parts & Service Request.</i> To request parts or
                        services you will provide your name, telephone number,
                        email address, physical address, and any additional
                        personal information you choose to provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Value Your Trade.</i> To value equipment you trade-in
                        with us, you will provide your name, telephone number,
                        email address, and any additional information you choose
                        to provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Online Bill Pay.</i> To sign up for online bill pay
                        services, you will provide your name, email address,
                        username, and password. Our online billing service is
                        provided by BillTrust whose privacy policy can be found{' '}
                        <a
                            href="https://www.billtrust.com/privacy-policy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-links hover:underline"
                        >
                            here.
                        </a>{' '}
                        To sign into online bill pay you will provide your
                        username and password.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Now. </i> To sign up for or into LiftOne Now
                        you will provide your name, email address, telephone
                        number, and physical address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Catalog Email Sign Up. </i> To sign up to
                        receive emails from the LiftOne catalog you will provide
                        your email address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Catalog Account. </i> To register an account
                        with LiftOne Catalog you will provide your name,
                        physical address, email address, telephone number,
                        username and password and any additional information you
                        choose to provide. To login to your account you will
                        provide your username and password.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Catalog Quote. </i> To Request a Quote on a
                        LiftOne Catalog purchase, you will provide your name,
                        physical address, email address, telephone number, and
                        any additional information you choose to provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>LiftOne Catalog Checkout. </i> To checkout and make a
                        purchase from the LiftOne catalog, you will provide your
                        name, physical address, email address, telephone number,
                        payment information, which may include your credit card
                        number, expiration date, security code, checking account
                        number, any additional information you choose to
                        provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Yale Vision Sign In. </i> Yale Vision is provided by
                        Hyster-Yale Group, Inc. To sign into Yale vision, you
                        will provide your username and password.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Yale Vision Equipment Activation. </i> To activate
                        Yale Vision equipment through Hyster-Yale Group, you
                        will provide your name, telephone number, and email
                        address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Yale Vision Mobile App. </i> To use or sign up to the
                        Yale Vision app, you will provide your username,
                        password, name, and email address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Hyster Fleet Tracker Sign In. </i> Hyster Fleet
                        Tracker is provided by Hyster-Yale Group, Inc. To sign
                        into Yale Vision, you will provide your username and
                        password.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Hyster Fleet Tracker Equipment Activation. </i> To
                        activate Hyster Fleet Tracker equipment through
                        Hyster-Yale Group, you will provide your name, telephone
                        number, and email address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Hyster Fleet Tracker Mobile App. </i> To use or sign
                        up to the Hyster Fleet Tracker app, you will provide
                        your username, password, name, and email address.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Contact Us. </i> To contact us, you will provide your
                        name, physical address, telephone number, email address,
                        and any additional information you choose to provide.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Chat. </i> If you use our chat feature, you will
                        provide your name, email address, and any information
                        you chose to provide in your message. Our chat feature
                        is provided by Intercom. Intercom may collect, record,
                        and store the information you provide in the chat.
                        Please review Intercom’s privacy policy{' '}
                        <a
                            href="https://www.intercom.com/legal/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-links hover:underline"
                        >
                            here.
                        </a>
                    </li>
                </ul>

                <h3 className="font-bold text-base ml-8 my-2">
                    BlueSkye Automation Site and Services
                </h3>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Contact Us. </i> To contact us, you will provide your
                        name, email address, telephone number, and any
                        additional information you choose to provide.
                    </li>
                </ul>

                <h2 className="font-bold text-base my-2">
                    Information as You Navigate Our Sites and Services
                </h2>
                <p className="mb-2">
                    We automatically collect certain Personal Information
                    through your use of the Sites and Services, such as the
                    following:
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Usage Information. </i> For example, the pages on the
                        Sites you access, the frequency of access, and what you
                        click on while on the Sites.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Device Information. </i> For example, hardware model,
                        operating system, application version number, and
                        browser.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Mobile Device Information. </i> Aggregated
                        information about whether the Site is accessed via a
                        mobile device or tablet, the device type, and the
                        carrier.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Location Information. </i> Location information from
                        visitors of the Sites on a city-regional basis.
                    </li>
                </ul>

                <h2 className="font-bold text-base my-2">Systems Data</h2>
                <p className="mb-2">
                    We may collect the following information through your use of
                    our web applications and Devices (collectively, “Digital
                    Offerings”). Please note that not all of this information
                    may be considered Personal Information.
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Device, Asset and Component Information. </i> For
                        example, we may collect model number, serial number,
                        order number, software and hardware version numbers,
                        performance, and configuration, including work tools and
                        other peripheral Devices attached to Assets.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Electronic Data. </i> For example, we may collect
                        sensor logs, trends, histograms, event data, other
                        alerts, digital state data, fault codes, idle time,
                        daily cumulative fuel consumption, emissions data,
                        service meter hours, electronic data files, downloaded
                        manually or automatically from an Asset, troubleshooting
                        data, and other data, depending on the client and Asset
                        and communication channel used by a Device.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Inspection Data. </i> For example, we may collect
                        results of inspections using a LiftOne or third-party
                        inspection system.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Device Location Information. </i> For example, we may
                        collect physical location of an Asset (e.g., determined
                        using satellite, GPS, cell phone tower, Bluetooth, or
                        Wi-Fi signals).
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Fluid Data. </i> For example, we may collect analysis
                        results of fluid samples (such as oil, hydraulic and
                        coolant fluids) obtained using LiftOne or third-party
                        tools.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Event Recorder Data. </i> For example, we may collect
                        location, speed, direction, and associated video
                        recordings, use of controls and positive train control
                        information.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Service and Maintenance History. </i> For example, we
                        may collect work orders (records of all maintenance,
                        repairs, parts purchases, replacements, and
                        modifications to an Asset), component life (history of
                        usage and wear life of component), maintenance
                        schedules, planned maintenance warranty coverage data,
                        maintenance and repair contracts, service intervals
                        (scheduled interval for planned maintenance of component
                        replacement activities for an Asset), component lists
                        (lists of parts that make up an Assets), and service
                        letters (describing special service actions recommended
                        by manufacturers to correct a known problem with an
                        Asset).
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Site and Environmental Conditions. </i> For example,
                        we may collect the type of work being done, conditions
                        of roads and tracks, altitude, climate, and material
                        tracking.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Patterns of Use. </i> For example, we may collect any
                        user-defined information related to a product you
                        provide to us through a Digital Offering.
                    </li>
                </ul>

                <h2 className="font-bold text-base my-2">Operations Data </h2>
                <p className="mb-2">
                    We may collect the following information through our
                    operations. Please note that not all of this information may
                    be considered Personal Information.
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Transactional Documents Information. </i> For
                        example, we may collect information contained in
                        tenders, invoices, purchase orders, sales contracts,
                        service contracts, e-forms, and other transactional
                        documentation generated as part of a business
                        relationship between us.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Client Information. </i> For example, we may collect
                        client name, address, industry, client category, and the
                        name, position, email address, telephone number of the
                        client’s designated contact person, and any information
                        relating to client parts, machine purchases, returns or
                        replacements.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Work Order Data. </i> For example, we may collect
                        information about the client, Assets involved, problems
                        identified, repairs performed, emissions data, fuel
                        usage, service meter hours, software and hardware
                        version numbers and installed attachments.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Payment Information. </i> For example, we may collect
                        your banking account details, debit card details, credit
                        card details, credit card type (i.e., Visa, Mastercard,
                        American Express), your billing address associated with
                        the credit card, your shipping address and other
                        information that is required to process the payments.
                    </li>
                </ul>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    2. HOW WE COLLECT INFORMATION
                </h1>
                <p className="mb-2">
                    We collect information in a variety of ways. For example, we
                    collect information that you provide to us through various
                    Contact Centers, information we collect through your use of
                    our products or services, and information we collect from
                    publicly available sources or third parties.
                </p>
                <h2 className="font-bold text-base my-2">
                    Information You Provide to Us
                </h2>
                <p className="mb-2">
                    We collect information you give us when you engage with us
                    and/or use our products or services. For example, we may
                    collect information when you interact with us, attend one of
                    our stands at a trade show, make an inquiry, place an order,
                    or contact our customer service. Please also see our{' '}
                    <a
                        href="https://cte1926.com/cookie-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-links hover:underline"
                    >
                        Cookie Policy
                    </a>{' '}
                    for more information on our use of cookies and other
                    tracking technologies.
                </p>
                <h2 className="font-bold text-base my-2">
                    Information Collected Through Your Use of Our Sites,
                    Products and Services
                </h2>
                <p className="mb-2">
                    In addition to the sources described in Section 1, we may
                    collect the following information through your use of our
                    Sites, products, and Services.
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        <i>Pre-Installed/Installed Applications/Devices. </i> We
                        may receive information directly via cellular, satellite
                        link, radio, or ethernet connection from Assets equipped
                        with a mobile application or through telematics and
                        similar devices (“Devices”) that were either
                        pre-installed or installed by us.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>Wearable Technology. </i> We may collect information
                        through fatigue monitoring Devices or RFID tags embedded
                        in hardhats or safety vests.
                    </li>
                </ul>
                <h2 className="font-bold text-base my-2">
                    Information from Third-Party Sources
                </h2>
                <p className="mb-2">
                    We may collect the following types of information from
                    publicly and commercially available sources as permitted by
                    law, which we may combine with other information we receive
                    from you.
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        Via LiftOne’s parent, affiliates, and subsidiaries,
                    </li>
                    <li className="pl-4 mb-2">
                        <i>
                            Via Hyster-Yale or its business affiliates or
                            contractors.
                        </i>{' '}
                        We may receive information from Hyster-Yale or its
                        business affiliates or contractors which have collected
                        information from you via: (i) cellular or satellite
                        link, or radio or Ethernet connection from Assets, which
                        may include System Data or Personal Information; or (ii)
                        Digital Offerings.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>From Component Manufacturers and OEMs:</i> We may
                        obtain System Data from manufacturers of the components
                        in your Assets. This information may be provided to us
                        automatically.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>From Asset Owners and Others:</i> We may receive
                        additional information from Asset owners, operators and
                        other persons who have management responsibility for an
                        Asset.
                    </li>
                    <li className="pl-4 mb-2">
                        <i>From Other Sources:</i> We may receive your
                        information from other sources, such as credit reference
                        agencies, public databases, joint marketing partners,
                        internet, social medial platforms (including from people
                        with whom you are friends or otherwise connected) and
                        from other third parties. We may collect or generate
                        information from troubleshooting data, from your service
                        providers (such as fluid analysts and site inspectors)
                        or from maintenance, inspection or warranty records.
                    </li>
                </ul>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    3. HOW WE USE YOUR INFORMATION
                </h1>
                <p className="mb-2">
                    We use the information we collect to provide the Services to
                    you, to improve our Sites and Services, and to protect our
                    legal rights. In addition, we may use the information we
                    collect to:
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        Process your account registration;
                    </li>
                    <li className="pl-4 mb-2">
                        Allow you to monitor the status of Assets;
                    </li>
                    <li className="pl-4 mb-2">
                        Process and fulfill your orders and requests, including
                        requests for quotes and training classes;
                    </li>
                    <li className="pl-4 mb-2">
                        Fulfill client support agreements;
                    </li>
                    <li className="pl-4 mb-2">
                        Perform maintenance and repairs;
                    </li>
                    <li className="pl-4 mb-2">Deliver Assets or parts;</li>
                    <li className="pl-4 mb-2">
                        Make recommendations regarding safety, Asset health,
                        maintenance, worksite efficiency and productivity
                        training for operators;
                    </li>
                    <li className="pl-4 mb-2">
                        Enhance the safety of machine operations, including by
                        tracking proximity to Assets, other objects or humans;
                    </li>
                    <li className="pl-4 mb-2">
                        Enable remote technician services, such as remote
                        troubleshooting and remote tuning;
                    </li>
                    <li className="pl-4 mb-2">
                        Provide you with location-based services and content;
                    </li>
                    <li className="pl-4 mb-2">
                        Send administrative or contractual information, for
                        example, information regarding our terms and conditions
                        of sale and rent and using out Sites and Services;
                    </li>
                    <li className="pl-4 mb-2">
                        Contact you regarding our products and services and to
                        send you marketing communications that we feel may be of
                        interest to you for which you may opt-out at any time;
                    </li>
                    <li className="pl-4 mb-2">
                        Communicate with you about our Sites or Services or to
                        inform you of any changes to our Sites or Services;
                    </li>
                    <li className="pl-4 mb-2">
                        Communicate with you about your purchase or rental of
                        Assets and your purchase of Asset parts;{' '}
                    </li>
                    <li className="pl-4 mb-2">
                        Provide support and customer service;{' '}
                    </li>
                    <li className="pl-4 mb-2">Conduct market research;</li>
                    <li className="pl-4 mb-2">
                        Perform data analytics, audits, improve products,
                        develop new products, enhance, improve or modify our
                        Sites and services, identify usage trends and operate
                        our business activities and for statistical analysis
                        based on aggregated and de-identified data, such as
                        bench marking reports;
                    </li>
                    <li className="pl-4 mb-2">
                        Provide services to our clients, manage workflow,
                        monitor repairs, project future maintenance and service,
                        and troubleshoot issues;
                    </li>
                    <li className="pl-4 mb-2">
                        Validate effectiveness of recommendations and resolve
                        complaints;
                    </li>
                    <li className="pl-4 mb-2">
                        Manage Assets and parts inventory in order to provide
                        you with machines, parts and services;
                    </li>
                    <li className="pl-4 mb-2">
                        Manage a fleet of owned or tented Assets;
                    </li>
                    <li className="pl-4 mb-2">
                        Maximize the efficiency of operations and increase
                        sales;
                    </li>
                    <li className="pl-4 mb-2">
                        Develop digital applications;{' '}
                    </li>
                    <li className="pl-4 mb-2">
                        Manage your account with us and general keep our records
                        up to date and to administer or relationship with you;
                    </li>
                    <li className="pl-4 mb-2">Review your job application;</li>
                    <li className="pl-4 mb-2">
                        Allow you to participate in sweepstakes, competitions,
                        or similar promotions and to administer these
                        activities. Some of these activities have additional
                        rules, which could contain additional information about
                        how we use and disclose your information, including
                        Personal Information. We suggest that you read any such
                        rules carefully;
                    </li>
                    <li className="pl-4 mb-2">
                        Protect the security and integrity of our Sites and
                        Services;
                    </li>
                    <li className="pl-4 mb-2">
                        Investigate, prevent, or take action regarding illegal
                        activities, suspected fraud, situations involving
                        potential threats to the physical safety of any person,
                        or violations of our agreements, and to otherwise
                        fulfill our legal obligations;
                    </li>
                    <li className="pl-4 mb-2">
                        Monitor compliance with and enforce this Privacy Policy
                        and any other applicable agreements and policies;
                    </li>
                    <li className="pl-4 mb-2">
                        Defend our legal rights and the rights of others;{' '}
                    </li>
                    <li className="pl-4 mb-2">
                        Fulfill any other purposes for which you provide it, or
                        any other purpose we deem necessary or appropriate;
                    </li>
                    <li className="pl-4 mb-2">
                        Efficiently maintain our business; and{' '}
                    </li>
                    <li className="pl-4 mb-2">Comply with applicable law. </li>
                </ul>
                <p className="mb-2">
                    We may use anonymized information (or de-identified
                    information), that was derived from Personal Information,
                    but in its anonymized form cannot be identified with a
                    specific individual. This de-identified information is not
                    considered Personal Information for purposes of this Privacy
                    Policy and is not covered by this Privacy Policy. However,
                    if such information is used or formatted in such a way that
                    it is possible for that information to be identified with a
                    specific individual, then it will be considered Personal
                    Information as appropriate.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    4. HOW WE SHARE YOUR INFORMATION
                </h1>
                <p className="mb-2">
                    We may share the information that we collect about you in
                    the following ways:
                </p>
                <ul className="list-disc px-20 mb-4">
                    <li className="pl-4 mb-2">
                        With our parent, subsidiary and affiliate entities, to
                        our manufacturers and their subsidiaries and affiliates,
                        and to contractors, service providers and other third
                        parties used to support our business and the business of
                        our manufacturers;
                    </li>
                    <li className="pl-4 mb-2">
                        With vendors who perform data or Site-related services
                        on our behalf (e.g., email, hosting, maintenance,
                        backup, analysis, etc.);{' '}
                    </li>
                    <li className="pl-4 mb-2">
                        With vendors to prepare, deploy and analyze advertising
                        content;
                    </li>
                    <li className="pl-4 mb-2">
                        To the extent that we are required to do so by law;
                    </li>
                    <li className="pl-4 mb-2">
                        In connection with any legal proceedings or prospective
                        legal proceedings;
                    </li>
                    <li className="pl-4 mb-2">
                        To establish, exercise, or defend our or a third party’s
                        legal rights and property, including providing
                        information to others for the purposes of fraud
                        prevention;
                    </li>
                    <li className="pl-4 mb-2">
                        With any person who we reasonably believe may apply to a
                        court or other competent authority for disclosure of
                        that Personal Information where, in our reasonable
                        opinion, such court or authority would be reasonably
                        likely to order disclosure of that Personal Information;
                    </li>
                    <li className="pl-4 mb-2">
                        With any other person or entity as part of any business
                        or asset sale, equity transaction, merger, acquisition,
                        bankruptcy, liquidation, or similar proceeding, or in
                        preparation for any of these events;{' '}
                    </li>
                    <li className="pl-4 mb-2">
                        With any other person or entity where you consent to the
                        disclosure; and
                    </li>
                    <li className="pl-4 mb-2">
                        For any other purpose disclosed by us when you provide
                        the Personal Information or for any other purpose we
                        deem necessary, including to protect the health or
                        safety of others.
                    </li>
                </ul>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    5. COOKIES AND OTHER TRACKING TECHNOLOGIES
                </h1>
                <p className="mb-2">
                    Like many companies, we use cookies and other tracking
                    technologies (such as pixels and web beacons). To learn more
                    about how we use cookies and to manage your cookie settings,
                    please see our{' '}
                    <a
                        href="https://cte1926.com/cookie-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-links hover:underline"
                    >
                        Cookie Policy.
                    </a>
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    6. “DO NOT TRACK” SIGNALS
                </h1>
                <p className="mb-2">
                    Some internet browsers incorporate a “Do Not Track” feature
                    that signals to websites you visit that you do not want to
                    have your online activity tracked. Given that there is not a
                    uniform way that browsers communicate the “Do Not Track”
                    signal, the Sites do not currently interpret, respond to, or
                    alter their practices when they receive “Do Not Track”
                    signals.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    7. CHOICES ABOUT YOUR INFORMATION
                </h1>
                <p className="mb-2">
                    To review or request changes to any of your Personal
                    Information, please contact us as described below. To
                    unsubscribe from our marketing emails, please click the
                    unsubscribe link included in the footer of our emails. You
                    also may submit a request to us using the contact
                    information described below.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">8. SECURITY</h1>
                <p className="mb-2">
                    We maintain commercially reasonable security measures to
                    protect the Personal Information we collect and store from
                    loss, misuse, destruction, or unauthorized access. However,
                    no security measure or modality of data transmission over
                    the Internet is 100% secure. Although we strive to use
                    commercially acceptable means to protect your Personal
                    Information, we cannot guarantee absolute security.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    9. THIRD PARTY LINKS
                </h1>
                <p className="mb-2">
                    The Sites and Services may contain links that will let you
                    leave the Sites and Services and access another website.
                    Linked websites are not under our control. This Privacy
                    Policy applies solely to Personal Information that is
                    acquired by us on this Sites and Services. We accept no
                    responsibility or liability for these other websites.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    10. CHILDREN’S PRIVACY
                </h1>
                <p className="mb-2">
                    The Sites and Services are not intended for children under
                    13 years of age. We do not knowingly collect, use, or
                    disclose personal information from children under 13.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    11. NOTICE TO CALIFORNIA RESIDENTS
                </h1>
                <p className="mb-2">
                    We do not disclose personal information obtained through our
                    Site or Services to third parties for their direct marketing
                    purposes. Accordingly, we have no obligations under
                    California Civil Code § 1798.83.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    12. ACCESSIBILITY
                </h1>
                <p className="mb-2">
                    We are committed to ensuring this Privacy Policy is
                    accessible to individuals with disabilities. If you wish to
                    access this Privacy Policy in an alternative format, please
                    contact us as described below.
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    13. HOW TO CONTACT US
                </h1>
                <p className="mb-2">
                    To contact us for questions or concerns about our privacy
                    policies or practices please contact us by calling us by
                    phone at 704-596-6700, by mail at 9000 Statesville Road,
                    Charlotte, NC 28269, Attn: Legal Department, or
                    electronically at{' '}
                    <a
                        className="text-links hover:underline"
                        href="mailto:privacy@weisigergroup.com"
                    >
                        privacy@weisigergroup.com.
                    </a>
                </p>

                <h1 className="font-bold text-lg mb-4 mt-8">
                    14. HYSTER-YALE DATA GOVERNANCE
                </h1>
                <p className="mb-2">
                    <a
                        href="https://www.hyster-yale.com/external-privacy-statement/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-links hover:underline"
                    >
                        Hyster-Yale’s External Privacy Statement
                    </a>{' '}
                    and any applicable Hyster-Yale Privacy Notices for its
                    digital offering subscriptions, describes Hyster-Yale’s
                    practices for collecting, sharing, and using data and
                    information relating to machines, products or other assets
                    and their associated worksites. By providing data and
                    information to a dealer as described herein, you agree to
                    its terms as it may be revised from time to time.
                </p>
            </div>
        </ContentLayout>
    )
}

export default PrivacyPolicyPage
